import React, { useState, useRef } from "react";
import {
  Upload,
  Input,
  message,
  Tabs,
  Spin,
  Popconfirm,
} from "antd";
import {
  InboxOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import useAxiosInstance from "../../../../../lib/useAxiosInstance";

const { Dragger } = Upload;
const { TextArea } = Input;

/* ---------------- FILE LIST COMPONENT ---------------- */

const FileList = ({ files, onDelete, modalRef }) => {
  if (!files || !files.length) return null;
 
  return (
    <div className="mt-4">
      <p className="text-sm font-medium mb-2 text-slate-700">
        Uploaded Files
      </p>

      <ul className="space-y-2">
        {files.map((file, index) => (
          <li
            key={index}
            className="flex items-center justify-between bg-slate-50 border rounded-md px-3 py-2"
          >
            <span className="text-sm text-slate-600 truncate max-w-[60%]">
              {file.name}
            </span>

            <div className="flex items-center gap-4">
              <a
                href={file.publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-600 text-sm font-medium hover:underline"
              >
                Download
              </a>

              <Popconfirm
                title="Delete this file?"
                description="All associated data will be permanently removed."
                okText="Delete"
                cancelText="Cancel"
                getPopupContainer={() => modalRef.current}
                onConfirm={() => onDelete(file)}
              >
                <DeleteOutlined className="text-red-500 cursor-pointer" />
              </Popconfirm>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */

const AddElectionDataModal = ({ survey, onClose }) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const modalRef = useRef(null);

  /* ---------------- STATE ---------------- */

  const [electionFile, setElectionFile] = useState(null);
  const [erFile, setErFile] = useState(null);
  const [electionComment, setElectionComment] = useState("");
  const [erComment, setErComment] = useState("");

  /* ---------------- FETCH SUMMARY ---------------- */

  const {
    data: summaryData,
    isLoading: summaryLoading,
  } = useQuery(
    ["electionDataSummary", survey?._id],
    async () => {
      const res = await axiosInstance.get(
        `/api/electionData/summary/${survey?._id}`
      );
      return res.data?.data;
    },
    {
      enabled: !!survey?._id,
    }
  );

  /* ---------------- UPLOAD MUTATIONS ---------------- */

  const uploadElectionData = useMutation(
    async () => {
      const formData = new FormData();
      formData.append("csvFile", electionFile);
      formData.append("surveyId", survey?._id);
      formData.append("comment", electionComment);

      const url =
        survey?.surveyType === "state"
          ? "/api/electionData/uploadStateElectionData"
          : "/api/electionData/uploadElectionData";

      return axiosInstance.post(url, formData);
    },
    {
      onSuccess: () => {
        message.success("Election data uploaded successfully");
        queryClient.invalidateQueries([
          "electionDataSummary",
          survey?._id,
        ]);
        setElectionFile(null);
        setElectionComment("");
      },
      onError: (error) => {
        const errorMsg =
          error?.response?.data?.message ||
          "Failed to upload election data";
        message.error(errorMsg);
      },
    }
  );

  const uploadERData = useMutation(
    async () => {
      const formData = new FormData();
      formData.append("csvFile", erFile);
      formData.append("surveyId", survey?._id);
      formData.append("comment", erComment);

      return axiosInstance.post(
        "/api/electionData/uploadERData",
        formData
      );
    },
    {
      onSuccess: () => {
        message.success(
          "Electoral roll data uploaded successfully"
        );
        queryClient.invalidateQueries([
          "electionDataSummary",
          survey?._id,
        ]);
        setErFile(null);
        setErComment("");
      },
      onError: (error) => {
        const errorMsg =
          error?.response?.data?.message ||
          "Failed to upload ER data";
        message.error(errorMsg);
      },
    }
  );

  /* ---------------- DELETE MUTATION ---------------- */

  const deleteFileMutation = useMutation(
    async ({ fileKey, type }) => {
      return axiosInstance.delete(
        "/api/electionData/deleteFile",
        {
          data: {
            surveyId: survey?._id,
            fileKey,
            type,
          },
        }
      );
    },
    {
      onSuccess: () => {
        message.success("File deleted successfully");
        queryClient.invalidateQueries([
          "electionDataSummary",
          survey?._id,
        ]);
      },
      onError: () =>
        message.error("Failed to delete file"),
    }
  );

  /* ---------------- HELPERS ---------------- */

  const uploadProps = (setter) => ({
    multiple: false,
    maxCount: 1,
    beforeUpload: () => false,
    onChange: ({ fileList }) =>
      setter(fileList[0]?.originFileObj || null),
    onRemove: () => setter(null),
  });

  const handleDelete = (file, type) => {
    deleteFileMutation.mutate({
      fileKey: file.key,
      type,
    });
  };

  /* ---------------- UI ---------------- */

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
    >
      <div className="w-full max-w-3xl bg-white rounded-xl shadow-xl p-6">

        {/* HEADER */}
        <div className="flex justify-between mb-4">
          <h2 className="text-base font-semibold">
            Election Data
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500"
          >
            ✕
          </button>
        </div>

        <Tabs defaultActiveKey="election">

          {/* FORM 20 TAB */}
          <Tabs.TabPane tab="Form 20" key="election">
            <Dragger {...uploadProps(setElectionFile)} disabled={uploadElectionData.isLoading}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p>Select a file or drag and drop</p>
              <p className="text-xs text-slate-400">
                Upload Excel / CSV file (max 30MB)
              </p>
            </Dragger>

            {summaryLoading ? (
              <Spin className="mt-4" />
            ) : (
              <FileList
                files={summaryData?.electionDataFiles}
                modalRef={modalRef}
                onDelete={(file) =>
                  handleDelete(file, "ELECTION")
                }
              />
            )}

            <TextArea
              className="mt-3"
              rows={3}
              placeholder="Comment (optional)"
              value={electionComment}
              onChange={(e) =>
                setElectionComment(e.target.value)
              }
            />

            <div className="mt-4 flex justify-end">
              {/* <button
                onClick={() =>
                  electionFile
                    ? uploadElectionData.mutate()
                    : message.error("Please upload a file")
                }
                className="bg-sky-600 text-white px-6 py-2 rounded-md text-sm"
              >
                Save
              </button> */}
              <button
                disabled={uploadElectionData.isLoading}
                onClick={() =>
                  electionFile
                    ? uploadElectionData.mutate()
                    : message.error("Please upload a file")
                }
                className="bg-sky-600 text-white px-6 py-2 rounded-md text-sm disabled:opacity-50"
              >
                {uploadElectionData.isLoading ? (
                  <Spin size="small" />
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </Tabs.TabPane>

          {/* ELECTORAL ROLL TAB */}
          {survey?.surveyType !== "state" && <Tabs.TabPane tab="Electoral Roll" key="er">
            <Dragger {...uploadProps(setErFile)} disabled={uploadERData.isLoading}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p>Select a file or drag and drop</p>
              <p className="text-xs text-slate-400">
                Upload Excel / CSV file (max 30MB)
              </p>
            </Dragger>

            {summaryLoading ? (
              <Spin className="mt-4" />
            ) : (
              <FileList
                files={summaryData?.erDataFiles}
                modalRef={modalRef}
                onDelete={(file) =>
                  handleDelete(file, "ER")
                }
              />
            )}

            <TextArea
              className="mt-3"
              rows={3}
              placeholder="Comment (optional)"
              value={erComment}
              onChange={(e) =>
                setErComment(e.target.value)
              }
            />

            <div className="mt-4 flex justify-end">
              {/* <button
                onClick={() =>
                  erFile
                    ? uploadERData.mutate()
                    : message.error("Please upload a file")
                }
                className="bg-indigo-600 text-white px-6 py-2 rounded-md text-sm"
              >
                Save
              </button> */}
              <button
                disabled={uploadERData.isLoading}
                onClick={() =>
                  erFile
                    ? uploadERData.mutate()
                    : message.error("Please upload a file")
                }
                className="bg-indigo-600 text-white px-6 py-2 rounded-md text-sm disabled:opacity-50"
              >
                {uploadERData.isLoading ? (
                  <Spin size="small" />
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </Tabs.TabPane>}

        </Tabs>
      </div>
    </div>
  );
};

export default AddElectionDataModal;
